import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import {
  interpreterStoreAtom,
  runtimeApplicationAtom,
  selectedApplicantAtom,
} from "@/store";
import apiCaller from "@/utils/api-caller";
import { parseFormData } from "@/utils/parser";
import { isAxiosError } from "axios";
import { toValidApplicantId } from "@/utils/applicant-id";

interface ApplicationSummaryProps {
  expression?: string;
  text?: string | null;
}

export function ApplicationSummary({
  expression,
  text,
}: ApplicationSummaryProps) {
  const interpreterStore = useAtomValue(interpreterStoreAtom);
  const selectedApplicant = useAtomValue(selectedApplicantAtom);
  const runtimeApplication = useAtomValue(runtimeApplicationAtom);

  const [summaryResult, setSummaryResult] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (text !== undefined) {
      setSummaryResult(text || "");
      return;
    }

    // 如果沒有設定 expression 就不執行
    if (!expression || !interpreterStore) return;

    const fetchSummary = async () => {
      setIsLoading(true);
      try {
        const rawValues = interpreterStore.getEntitiesValues?.() ?? {};
        const schema = interpreterStore.schema ?? { root: [], entities: {} };
        const formData = parseFormData(rawValues, schema).data;

        // 切到 preview 時只會執行這一次 request
        const response = await apiCaller.post("/execution/fetch", {
          function: expression,
          formData,
          serialNumber: runtimeApplication?.serialNumber,
          applicantId:
            toValidApplicantId(selectedApplicant?.id) ??
            toValidApplicantId(runtimeApplication?.applicantId) ??
            toValidApplicantId(runtimeApplication?.submittedBy),
        });

        const resultStr =
          typeof response.data === "object"
            ? JSON.stringify(response.data)
            : String(response.data);

        setSummaryResult(resultStr);
      } catch (error) {
        console.error("Failed to fetch summary preview", error);
        if (isAxiosError(error)) {
          setSummaryResult("Error:" + error.response?.data?.message);
        } else if (error instanceof Error) {
          setSummaryResult("Error:" + error.message);
        } else {
          setSummaryResult("Error fetching summary.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    void fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    expression,
    interpreterStore,
    selectedApplicant?.id,
    runtimeApplication?.applicantId,
    runtimeApplication?.submittedBy,
  ]);

  if (!expression && text === undefined) return null;

  return (
    <div className="flex gap-4">
      <div className="flex-1 flex flex-col gap-2.5">
        <p className="text-dark">{"Summary"}</p>
        <span className="flex-1 font-body-medium-semibold text-gray-900">
          {isLoading ? "Loading summary..." : summaryResult || "No output"}
        </span>
      </div>
    </div>
  );
}
